<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import countries from '@/data/countries.json'
import type {
    BetaLinkBuildingLink,
    BetaLinkBuildingLinkInput,
    BetaLinkBuildingRouteType,
} from '@/types/beta-dashboard'

const props = defineProps<{
    open: boolean
    link?: BetaLinkBuildingLink | null
    defaultDdi?: string | number
    saving?: boolean
}>()

const emit = defineEmits<{
    close: []
    save: [link: BetaLinkBuildingLinkInput]
}>()

const error = ref('')
const draft = ref<BetaLinkBuildingLinkInput>(emptyLink())

function emptyLink(): BetaLinkBuildingLinkInput {
    return {
        route_name: '',
        route_path: '',
        type: 7,
        target_blank: 1,
        typeRoute: 'route',
        phone: '',
        ddi: props.defaultDdi ?? 30,
    }
}

watch(
    [() => props.open, () => props.link],
    ([open, link]) => {
        if (!open) return
        draft.value = link ? JSON.parse(JSON.stringify(link)) : emptyLink()
        error.value = ''
    },
    { immediate: true },
)

const isEditing = computed(() => Boolean(props.link?.id))

const setRouteType = (event: Event) => {
    draft.value.typeRoute = (event.target as HTMLSelectElement).value as BetaLinkBuildingRouteType
}

const save = () => {
    const name = draft.value.route_name.trim()
    if (!name) {
        error.value = 'Informe o nome do link.'
        return
    }

    if (draft.value.typeRoute === 'route' && !draft.value.route_path.trim()) {
        error.value = 'Informe o endereço do link.'
        return
    }

    if (draft.value.typeRoute === 'whats' && !draft.value.phone.trim()) {
        error.value = 'Informe o número do WhatsApp.'
        return
    }

    error.value = ''
    emit('save', JSON.parse(JSON.stringify({
        ...draft.value,
        route_name: name,
        target_blank: draft.value.target_blank ? 1 : 0,
    })))
}
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="beta-link-dialog" role="dialog" aria-modal="true">
            <button type="button" class="beta-link-dialog__backdrop" aria-label="Fechar" @click="emit('close')"></button>

            <form class="beta-link-dialog__panel" @submit.prevent="save">
                <header>
                    <div>
                        <p>Link Building</p>
                        <h2>{{ isEditing ? 'Editar link' : 'Adicionar link' }}</h2>
                    </div>
                    <button type="button" class="is-close" aria-label="Fechar" @click="emit('close')">×</button>
                </header>

                <div class="beta-link-dialog__body">
                    <label class="beta-link-dialog__field">
                        <span>Nome</span>
                        <input v-model="draft.route_name" type="text" maxlength="120">
                    </label>

                    <label class="beta-link-dialog__field">
                        <span>Link para</span>
                        <select :value="draft.typeRoute" @change="setRouteType">
                            <option value="route">URL</option>
                            <option value="whats">WhatsApp</option>
                        </select>
                    </label>

                    <label v-if="draft.typeRoute === 'route'" class="beta-link-dialog__field">
                        <span>Endereço (URL)</span>
                        <input v-model="draft.route_path" type="text" placeholder="https://">
                    </label>

                    <div v-else class="beta-link-dialog__phone">
                        <label class="beta-link-dialog__field">
                            <span>País</span>
                            <select v-model="draft.ddi">
                                <option v-for="country in countries" :key="country.id" :value="country.id">
                                    {{ country.emoji }} (+{{ country.phonecode }})
                                </option>
                            </select>
                        </label>
                        <label class="beta-link-dialog__field">
                            <span>Número de telefone</span>
                            <input v-model="draft.phone" type="tel" placeholder="(##) #####-####">
                        </label>
                    </div>

                    <label class="beta-link-dialog__target">
                        <input v-model="draft.target_blank" type="checkbox" :true-value="1" :false-value="0">
                        <span>Abrir em nova aba</span>
                    </label>

                    <p v-if="error" class="beta-link-dialog__error">{{ error }}</p>
                </div>

                <footer>
                    <button type="button" class="is-cancel" :disabled="saving" @click="emit('close')">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="saving">
                        {{ saving ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Adicionar' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-link-dialog {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: grid;
    place-items: center;
    padding: 18px;
}

.beta-link-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 35, 57, 0.64);
    backdrop-filter: blur(7px);
}

.beta-link-dialog__panel {
    position: relative;
    display: grid;
    width: min(540px, 100%);
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
}

.beta-link-dialog__panel > header,
.beta-link-dialog__panel > footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-link-dialog__panel > header {
    border-bottom: 1px solid #e8edf3;
}

.beta-link-dialog__panel > header p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-link-dialog__panel > header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 700;
}

.beta-link-dialog__panel .is-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-link-dialog__body {
    display: grid;
    gap: 12px;
    padding: 15px 18px;
}

.beta-link-dialog__field {
    display: grid;
    gap: 6px;
}

.beta-link-dialog__field > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-link-dialog__field input,
.beta-link-dialog__field select {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    background: #fff;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-link-dialog__field input:focus,
.beta-link-dialog__field select:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-link-dialog__phone {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 9px;
}

.beta-link-dialog__target {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #60738d;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.beta-link-dialog__target input {
    accent-color: var(--main-color);
}

.beta-link-dialog__error {
    color: #b24c4c;
    font-size: 10px;
    font-weight: 700;
}

.beta-link-dialog__panel > footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
    background: #fbfcfe;
}

.beta-link-dialog__panel > footer button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    padding: 0 15px;
    font-size: 10px;
    font-weight: 700;
}

.beta-link-dialog__panel > footer .is-cancel {
    color: #687b94;
}

.beta-link-dialog__panel > footer .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-link-dialog__panel button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 520px) {
    .beta-link-dialog {
        place-items: end center;
        padding: 0;
    }

    .beta-link-dialog__panel {
        border-radius: 22px 22px 0 0;
    }

    .beta-link-dialog__phone {
        grid-template-columns: 1fr;
    }
}
</style>
